import { useState, useEffect, useRef, useCallback, MutableRefObject } from 'react';
import { ChatAttachment, Message, SessionBlock } from '../types/chat';
import { AgentChatClient } from '../services/AgentChatClient';

interface SessionSummary {
  sessionId: string;
  title: string | null;
  preview: string;
  date: string;
}

export interface UseUnifiedThreadResult {
  blocks: SessionBlock[];
  currentSessionId: string;
  isLoadingOlder: boolean;
  loadError: Error | null;
  hasMoreSessions: boolean;
  loadOlderSession: () => Promise<void>;
  addMessage: (message: Message) => void;
  replaceLastMessage: (message: Message) => void;
  /** True immediately after loadOlderSession resolves if that load was served from the prefetch cache (instant, no network wait). Read right after a blocks-length change — always current, since it's set synchronously alongside the state update that grew blocks. */
  lastLoadWasCacheHitRef: MutableRefObject<boolean>;
  /**
   * The client the loaded session is about, from its stored activeClientId.
   * Null for a new session or one with no client yet, which is what lets the
   * caller fall back to the *page's* client instead of showing a stale name.
   */
  loadedSessionClient: { id: string; name: string } | null;
}

// How many older sessions to keep pre-fetched (in the background, silently)
// ahead of the one currently at the front of the queue. Deep enough that a
// few quick consecutive scroll-backs in one sitting all stay instant;
// shallow enough that it's still a bounded, small amount of extra fetching.
const PREFETCH_DEPTH = 3;

/** Convert a raw history entry (no id/timestamp) to a full Message. */
function toMessage(
  raw: { role: 'user' | 'assistant'; content: string; attachments?: ChatAttachment[]; meetingLink?: Message['meetingLink']; emailLink?: Message['emailLink'] },
  sessionId: string,
  index: number,
): Message {
  return {
    id: `${sessionId}-${index}-${Date.now()}`,
    role: raw.role,
    content: raw.content,
    timestamp: new Date(),
    sessionId,
    attachments: raw.attachments,
    meetingLink: raw.meetingLink,
    emailLink: raw.emailLink,
  };
}

export function useUnifiedThread(
  client: AgentChatClient,
  currentSessionId: string,
): UseUnifiedThreadResult {
  const [blocks, setBlocks] = useState<SessionBlock[]>([]);
  const [queue, setQueue] = useState<SessionSummary[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [loadedSessionClient, setLoadedSessionClient] = useState<{ id: string; name: string } | null>(null);
  const isFirstMount = useRef(true);
  const currentSessionIdRef = useRef(currentSessionId);
  useEffect(() => {
    currentSessionIdRef.current = currentSessionId;
  }, [currentSessionId]);

  // Rolling N-deep prefetch: as soon as we know which sessions are next in
  // the queue, silently fetch their messages in the background and hold them
  // here, keyed by sessionId. loadOlderSession checks this first — a cache
  // hit commits instantly (no network wait, no loading indicator at all)
  // instead of the normal fetch-then-render path. Scoped deliberately to
  // "the queue already has known next items" — it never triggers the
  // queue/cursor-page fetch itself, so there's no risk of two fetches racing
  // to load the same next page.
  const prefetchCacheRef = useRef<Map<string, Message[]>>(new Map());
  const prefetchInFlightRef = useRef<Set<string>>(new Set());
  const lastLoadWasCacheHitRef = useRef(false);

  useEffect(() => {
    const sessionAtStart = currentSessionId;
    queue.slice(0, PREFETCH_DEPTH).forEach(candidate => {
      if (prefetchCacheRef.current.has(candidate.sessionId)) return;
      if (prefetchInFlightRef.current.has(candidate.sessionId)) return;

      prefetchInFlightRef.current.add(candidate.sessionId);
      client.getConversationHistory(candidate.sessionId)
        .then(history => {
          if (currentSessionIdRef.current !== sessionAtStart) return; // switched sessions — discard
          const messages: Message[] = history.messages.map((m, i) =>
            toMessage(m, candidate.sessionId, i),
          );
          prefetchCacheRef.current.set(candidate.sessionId, messages);
        })
        .catch(() => { /* silent — the real trigger just falls back to a normal fetch */ })
        .finally(() => { prefetchInFlightRef.current.delete(candidate.sessionId); });
    });
  }, [queue, client, currentSessionId]);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      const [history, sessions] = await Promise.all([
        client.getConversationHistory(currentSessionId),
        client.getSessions(),
      ]);
      if (cancelled) return;

      // Reset-then-set in one go: a session with no stored client clears the
      // previous session's value rather than leaving it to linger.
      setLoadedSessionClient(history.conversationClient ?? null);

      const messages: Message[] = history.messages.map((m, i) =>
        toMessage(m, currentSessionId, i),
      );
      const older: SessionSummary[] = sessions.sessions
        .filter(s => s.sessionId !== currentSessionId)
        .map(s => ({ sessionId: s.sessionId, title: s.title, preview: s.preview, date: s.updatedAt }));

      const currentSummary = sessions.sessions.find(s => s.sessionId === currentSessionId);
      const currentBlock: SessionBlock = {
        sessionId: currentSessionId,
        title: currentSummary?.title ?? null,
        preview: '',
        date: new Date().toISOString(),
        messages,
      };

      if (isFirstMount.current) {
        isFirstMount.current = false;
      }
      // Fresh start / resume: show only this session (do not keep prior continuum)
      setBlocks([currentBlock]);
      setQueue(older);
      setNextCursor(sessions.nextCursor);
      prefetchCacheRef.current.clear();
      prefetchInFlightRef.current.clear();
    }

    init();
    return () => { cancelled = true; };
  }, [currentSessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadOlderSession = useCallback(async () => {
    if (isLoadingOlder) return;

    const sessionAtStart = currentSessionId;
    const isStale = () => currentSessionIdRef.current !== sessionAtStart;

    // Default to "not a cache hit" up front — the one branch below that IS a
    // cache hit overrides it. Read by ChatInterface right after blocks grows,
    // to decide whether a "loaded" confirmation is even worth showing (an
    // instant load has nothing to confirm).
    lastLoadWasCacheHitRef.current = false;

    // Cache hit — already fetched in the background, so commit immediately.
    // isLoadingOlder is never toggled on this path: there's no wait to smooth
    // over, and no window during which a competing scroll could occur.
    const candidate = queue[0];
    const cachedMessages = candidate ? prefetchCacheRef.current.get(candidate.sessionId) : undefined;
    if (candidate && cachedMessages) {
      prefetchCacheRef.current.delete(candidate.sessionId);
      lastLoadWasCacheHitRef.current = true;
      setQueue(prev => prev.slice(1));
      setBlocks(prev => [
        { sessionId: candidate.sessionId, title: candidate.title, preview: candidate.preview, date: candidate.date, messages: cachedMessages },
        ...prev,
      ]);
      return;
    }

    setIsLoadingOlder(true);
    setLoadError(null);

    try {
      let currentQueue = queue;
      let cursor = nextCursor;

      // Fetch more session summaries if the queue is empty but there are more pages
      if (currentQueue.length === 0) {
        if (!cursor) return;
        const result = await client.getSessions(cursor);
        if (isStale()) return; // user switched conversations while this was in flight

        currentQueue = result.sessions
          .filter(s => s.sessionId !== currentSessionId)
          .map(s => ({ sessionId: s.sessionId, title: s.title, preview: s.preview, date: s.updatedAt }));
        cursor = result.nextCursor;
        setNextCursor(cursor);
        setQueue(currentQueue);
      }

      if (currentQueue.length === 0) return;

      const [next, ...rest] = currentQueue;
      setQueue(rest);

      const history = await client.getConversationHistory(next.sessionId);
      if (isStale()) return; // user switched conversations while this was in flight

      const messages: Message[] = history.messages.map((m, i) =>
        toMessage(m, next.sessionId, i),
      );

      setBlocks(prev => [
        { sessionId: next.sessionId, title: next.title, preview: next.preview, date: next.date, messages },
        ...prev,
      ]);
    } catch (err) {
      if (!isStale()) {
        setLoadError(err instanceof Error ? err : new Error(String(err)));
      }
    } finally {
      setIsLoadingOlder(false);
    }
  }, [queue, nextCursor, isLoadingOlder, currentSessionId, client]);

  const addMessage = useCallback((message: Message) => {
    setBlocks(prev => prev.map(block =>
      block.sessionId === currentSessionId
        ? { ...block, messages: [...block.messages, { ...message, sessionId: currentSessionId }] }
        : block
    ));
  }, [currentSessionId]);

  const replaceLastMessage = useCallback((message: Message) => {
    setBlocks(prev => prev.map(block =>
      block.sessionId === currentSessionId && block.messages.length > 0
        ? { ...block, messages: [...block.messages.slice(0, -1), { ...message, sessionId: currentSessionId }] }
        : block
    ));
  }, [currentSessionId]);

  const hasMoreSessions = queue.length > 0 || nextCursor !== null;

  return {
    blocks,
    currentSessionId,
    isLoadingOlder,
    loadError,
    hasMoreSessions,
    loadOlderSession,
    addMessage,
    replaceLastMessage,
    lastLoadWasCacheHitRef,
    loadedSessionClient,
  };
}
